import styled from '@emotion/styled';
import type { CurrentLocationResponse } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { MapType } from '@racemap/utilities/consts/events';
import type { PointWithOffsetTimeNumber } from '@racemap/utilities/functions/codec';
import { getShadowtrack } from '@racemap/utilities/functions/map';
import type { UnitType } from '@racemap/utilities/types/units';
import { simplify } from '@turf/simplify';
import { Card, Splitter } from 'antd';
import type { Feature, Point, Position } from 'geojson';
import type { Immutable } from 'immer';
import type { Map as MapboxMap } from 'mapbox-gl';
import { type FC, useMemo, useRef, useState } from 'react';
import { IconDownload, IconExpand, IconMap } from 'src/components/BasicComponents/Icons';
import { BasicMap, Layers } from 'src/components/BasicComponents/Maps';
import {
  LegendDot,
  LegendGroup,
  LegendItem,
  MapLegend,
} from 'src/components/BasicComponents/Maps/Controls';
import { AUTOFOCUS } from 'src/components/BasicComponents/Maps/helpers';
import { Switch } from 'src/components/BasicComponents/Switch';
import { getBounds } from 'src/lib/geoUtils/getBounds';
import { getPlayerUrl } from 'src/lib/getPlayerUrl';
import { getDefaultViewState } from 'src/lib/map-helpers';
import type { CurrentEvent } from 'src/store/events/events_reducers';
import { CurrentAPIColorTable } from './consts';
import { GapsChart } from './GapsChart/GapsChart';
import type { CollectedSample } from './types';

// Minimum (and initial) width of the gaps-chart panel: the natural 400/320 aspect at the
// card's 540px height. The divider can only widen the chart from here; the chart's SVG
// re-derives its own scales for any larger width without stretching.
const GAPS_MIN_WIDTH = Math.round((540 * 400) / 320);

// ─── MapCard ──────────────────────────────────────────────────────────────────

export interface MapCardProps {
  currentEvent: Immutable<CurrentEvent>;
  rawStarterPoints: Array<PointWithOffsetTimeNumber>;
  rawLeaderPoints: Array<PointWithOffsetTimeNumber>;
  collectedSamples: Array<CollectedSample>;
  hoveredChartTimestamp: number | null;
  collectedResponses: Array<CurrentLocationResponse>;
  selectedStarterId: string | null;
  selectedLeaderId: string | null;
  avgSpeedWindowSeconds: number;
  unitType: UnitType;
}

export const MapCard: FC<MapCardProps> = ({
  currentEvent,
  rawStarterPoints,
  rawLeaderPoints,
  collectedSamples,
  hoveredChartTimestamp,
  collectedResponses,
  selectedStarterId,
  selectedLeaderId,
  avgSpeedWindowSeconds,
  unitType,
}) => {
  const [isDayMode, setIsDayMode] = useState(false);
  const [useSimplifiedTrack, setUseSimplifiedTrack] = useState<boolean>(true);
  const mapRef = useRef<MapboxMap | null>(null);

  const fullShadowtrack = getShadowtrack(currentEvent);
  const trackLengthKm = fullShadowtrack?.properties?.length ?? 0;
  const [showCorridor, setShowCorridor] = useState<boolean>(trackLengthKm <= 200);

  const reversedResponses = useMemo(() => collectedResponses.toReversed(), [collectedResponses]);
  const entryIdx = useMemo(
    () =>
      hoveredChartTimestamp != null
        ? reversedResponses.findIndex(
            (r) => r.resultTime <= new Date(hoveredChartTimestamp).toISOString(),
          )
        : reversedResponses.length > 0
          ? 0
          : -1,
    [hoveredChartTimestamp, reversedResponses],
  );
  const entry = entryIdx >= 0 ? reversedResponses[entryIdx] : null;
  const starters = entry?.starters ?? [];
  const gapChartStarterCurrent =
    starters.find((s) => s.id === selectedStarterId) ?? starters[0] ?? null;
  const gapChartLeaderCurrent = starters.find((s) => s.current.isRef === true) ?? null;
  const gapChartResultTimeInMillis = entry != null ? new Date(entry.resultTime).getTime() : null;

  const starterCutoffMillis =
    gapChartStarterCurrent != null ? new Date(gapChartStarterCurrent.current.time).getTime() : null;
  const leaderCutoffMillis =
    gapChartLeaderCurrent != null ? new Date(gapChartLeaderCurrent.current.time).getTime() : null;

  const slicedStarterPoints = useMemo(
    () =>
      starterCutoffMillis != null
        ? rawStarterPoints.filter((p) => p.time <= starterCutoffMillis)
        : [],
    [rawStarterPoints, starterCutoffMillis],
  );
  const slicedLeaderPoints = useMemo(
    () =>
      leaderCutoffMillis != null ? rawLeaderPoints.filter((p) => p.time <= leaderCutoffMillis) : [],
    [rawLeaderPoints, leaderCutoffMillis],
  );

  const simplifiedShadowtrack = useMemo(() => {
    if (fullShadowtrack == null) return null;
    return simplify(fullShadowtrack, { tolerance: 0.0001, highQuality: true });
  }, [fullShadowtrack]);

  const trackToRender = useSimplifiedTrack
    ? simplifiedShadowtrack || fullShadowtrack
    : fullShadowtrack;

  const rawStarterPointPositions = useMemo(
    () => rawStarterPoints.map((point): Position => [point.lng, point.lat]),
    [rawStarterPoints],
  );
  const rawReferencePointPositions = useMemo(
    () => rawLeaderPoints.map((point): Position => [point.lng, point.lat]),
    [rawLeaderPoints],
  );

  // Custom "zoom to extents": the built-in mapbox fit control lives in the map's own control
  // layer, so we drive the same fit ourselves from the grouped button — fitting the shadow track
  // plus any rendered raw points.
  const handleZoomToExtent = () => {
    const map = mapRef.current;
    if (map == null) return;
    const pointFeatures = [...rawStarterPointPositions, ...rawReferencePointPositions].map(
      (coordinates): Feature<Point> => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates },
        properties: {},
      }),
    );
    const bounds = getBounds([trackToRender, ...pointFeatures]);
    if (bounds == null) return;
    map.fitBounds(
      [
        [bounds.minLng, bounds.minLat],
        [bounds.maxLng, bounds.maxLat],
      ],
      { padding: 80, duration: 600 },
    );
  };

  const hoveredSample = useMemo(() => {
    if (hoveredChartTimestamp == null || collectedSamples.length === 0) return null;
    return collectedSamples.reduce<CollectedSample | null>((best, sample) => {
      const t = new Date(sample.resultTime).getTime();
      if (best == null) return sample;
      return Math.abs(t - hoveredChartTimestamp) <
        Math.abs(new Date(best.resultTime).getTime() - hoveredChartTimestamp)
        ? sample
        : best;
    }, null);
  }, [hoveredChartTimestamp, collectedSamples]);

  const hoveredGpsPoint = useMemo((): Array<Position> => {
    if (hoveredSample == null) return [];
    const lng = hoveredSample.values['current.lng'];
    const lat = hoveredSample.values['current.lat'];
    if (typeof lng !== 'number' || typeof lat !== 'number') return [];
    return [[lng, lat]];
  }, [hoveredSample]);

  const hoveredShadowtrackPoint = useMemo((): Array<Position> => {
    if (hoveredSample == null) return [];
    const lng = hoveredSample.values['current.lngSt'];
    const lat = hoveredSample.values['current.latSt'];
    if (typeof lng !== 'number' || typeof lat !== 'number') return [];
    return [[lng, lat]];
  }, [hoveredSample]);

  const hoveredRefGpsPoint = useMemo((): Array<Position> => {
    if (hoveredSample == null) return [];
    const lng = hoveredSample.values['ref.lng'];
    const lat = hoveredSample.values['ref.lat'];
    if (typeof lng !== 'number' || typeof lat !== 'number') return [];
    return [[lng, lat]];
  }, [hoveredSample]);

  const hoveredRefShadowtrackPoint = useMemo((): Array<Position> => {
    if (hoveredSample == null) return [];
    const lng = hoveredSample.values['ref.lngSt'];
    const lat = hoveredSample.values['ref.latSt'];
    if (typeof lng !== 'number' || typeof lat !== 'number') return [];
    return [[lng, lat]];
  }, [hoveredSample]);

  return (
    <StyledMapCard $daymode={isDayMode} styles={mapCardStyles}>
      <Splitter style={{ height: '100%' }}>
        <Splitter.Panel defaultSize={GAPS_MIN_WIDTH} min={GAPS_MIN_WIDTH} max="70%">
          <GapsSection>
            <GapsChart
              starterCurrent={gapChartStarterCurrent}
              leaderCurrent={gapChartLeaderCurrent}
              resultTimeInMillis={gapChartResultTimeInMillis}
              rawStarterPoints={slicedStarterPoints}
              rawLeaderPoints={slicedLeaderPoints}
              avgSpeedWindowSeconds={avgSpeedWindowSeconds}
              unitType={unitType}
              isDayMode={isDayMode}
              eventName={currentEvent.name}
            />
          </GapsSection>
        </Splitter.Panel>
        <Splitter.Panel>
          <MapSection>
            <MapControlGroup>
              <MapControlButton type="button" onClick={handleZoomToExtent} title="Zoom to extents">
                <IconExpand />
              </MapControlButton>
              {
                <MapControlLink
                  href={getPlayerUrl(currentEvent, {
                    starterIds: [selectedStarterId, selectedLeaderId].filter(
                      (id): id is string => id != null,
                    ),
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open selected starters in player"
                >
                  <IconMap />
                </MapControlLink>
              }
              <MapControlLink
                href={`/api/events/${currentEvent.id}/geo/shadow.chunked.bin`}
                download={`${currentEvent.id}.shadow.chunked.bin`}
                title="Download shadow track (.shadow.chunked.bin)"
              >
                <IconDownload />
              </MapControlLink>
            </MapControlGroup>
            <MapLegend side="right" title="Map Legend" offsetTop={200}>
              <LegendItem>
                <Switch value={isDayMode} onChange={setIsDayMode} label="Day mode" />
              </LegendItem>

              <LegendGroup>Participant</LegendGroup>
              <LegendItem>
                <LegendDot color={CurrentAPIColorTable.participant} />
                <span>Raw GPS points</span>
              </LegendItem>
              <LegendItem>
                <LegendDot color={CurrentAPIColorTable.participantHover} />
                <span>
                  Hover: GPS coords (<code>lat</code>, <code>lng</code>)
                </span>
              </LegendItem>
              <LegendItem>
                <LegendDot color={CurrentAPIColorTable.participantHoverSt} />
                <span>
                  Hover: Shadowtrack (<code>latSt</code>, <code>lngSt</code>)
                </span>
              </LegendItem>

              <LegendGroup>Leader | Reference</LegendGroup>
              <LegendItem>
                <LegendDot color={CurrentAPIColorTable.leader} />
                <span>Raw GPS points</span>
              </LegendItem>
              <LegendItem>
                <LegendDot color={CurrentAPIColorTable.leaderHover} />
                <span>
                  Hover: GPS coords (<code>lat</code>, <code>lng</code>)
                </span>
              </LegendItem>
              <LegendItem>
                <LegendDot color={CurrentAPIColorTable.leaderHoverSt} />
                <span>
                  Hover: Shadowtrack (<code>latSt</code>, <code>lngSt</code>)
                </span>
              </LegendItem>

              <LegendGroup>Route</LegendGroup>
              <LegendItem>
                <svg
                  width="20"
                  height="14"
                  viewBox="0 0 20 14"
                  fill="none"
                  style={{ flexShrink: 0 }}
                  aria-hidden="true"
                >
                  <rect
                    x="0"
                    y="2"
                    width="20"
                    height="10"
                    fill={CurrentAPIColorTable.shadowTrackCorridorFill}
                  />
                  <line
                    x1="0"
                    y1="7"
                    x2="20"
                    y2="7"
                    stroke={CurrentAPIColorTable.shadowTrackCorridor}
                    strokeWidth="1"
                  />
                </svg>
                <span>Mapping distance corridor</span>
                <Switch value={showCorridor} onChange={setShowCorridor} label="Show" />
              </LegendItem>
              <LegendItem>
                <svg
                  width="20"
                  height="14"
                  viewBox="0 0 20 14"
                  fill="none"
                  style={{ flexShrink: 0 }}
                  aria-hidden="true"
                >
                  {useSimplifiedTrack ? (
                    <path
                      d="M 0 7 L 20 7"
                      stroke={RacemapColors.PaleBlue}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  ) : (
                    <path
                      d="M 0 7 L 3 7 L 5 2 L 7 12 L 9 7 L 11 7 L 13 3 L 15 11 L 17 7 L 20 7"
                      stroke={RacemapColors.PaleBlue}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  )}
                </svg>
                <span>Shadowtrack</span>
                <Switch
                  value={useSimplifiedTrack}
                  onChange={setUseSimplifiedTrack}
                  label="Simplified"
                />
              </LegendItem>
            </MapLegend>
            <MapViewport>
              <BasicMap
                controls={{ navigation: true, fitToViewport: false }}
                onMapLoad={(map) => {
                  mapRef.current = map;
                }}
                mapStyle={isDayMode ? MapType.ROADMAP : MapType.DARK}
                defaultViewState={{
                  ...getDefaultViewState(),
                  padding: { top: 80, right: 80, bottom: 80, left: 80 },
                }}
              >
                <Layers.TrackLayer
                  tracks={showCorridor && trackToRender != null ? [trackToRender] : []}
                  color={CurrentAPIColorTable.shadowTrackCorridor}
                  radiusFillColor={CurrentAPIColorTable.shadowTrackCorridorFill}
                  radius={currentEvent.playerOptions.mappingDistance}
                />
                <Layers.TrackLayer
                  tracks={trackToRender != null ? [trackToRender] : []}
                  autofocus={AUTOFOCUS.ONCE}
                  color={CurrentAPIColorTable.shadowTrack}
                />
                <Layers.PointLayer
                  points={rawStarterPointPositions}
                  radius={2}
                  color={CurrentAPIColorTable.participant}
                  fillColor={CurrentAPIColorTable.participant}
                />
                <Layers.PointLayer
                  points={rawReferencePointPositions}
                  radius={2}
                  color={CurrentAPIColorTable.leader}
                  fillColor={CurrentAPIColorTable.leader}
                />
                <Layers.PointLayer
                  points={hoveredGpsPoint}
                  radius={7}
                  color={CurrentAPIColorTable.participantHover}
                  fillColor={CurrentAPIColorTable.participantHoverFill}
                />
                <Layers.PointLayer
                  points={hoveredShadowtrackPoint}
                  radius={7}
                  color={CurrentAPIColorTable.participantHoverSt}
                  fillColor={CurrentAPIColorTable.participantHoverStFill}
                />
                <Layers.PointLayer
                  points={hoveredRefGpsPoint}
                  radius={7}
                  color={CurrentAPIColorTable.leaderHover}
                  fillColor={CurrentAPIColorTable.leaderHoverFill}
                />
                <Layers.PointLayer
                  points={hoveredRefShadowtrackPoint}
                  radius={7}
                  color={CurrentAPIColorTable.leaderHoverSt}
                  fillColor={CurrentAPIColorTable.leaderHoverStFill}
                />
              </BasicMap>
            </MapViewport>
          </MapSection>
        </Splitter.Panel>
      </Splitter>
    </StyledMapCard>
  );
};

// ─── Styled components ────────────────────────────────────────────────────────

const mapCardStyles = { body: { height: '100%', padding: 0 } } as const;

const StyledMapCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== '$daymode',
})<{ $daymode: boolean }>`
  height: 540px;
  position: relative;
  overflow: hidden;
  background: ${({ $daymode }) => ($daymode ? '#f0f0f0' : '#000')};

`;

const MapSection = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const GapsSection = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const MapViewport = styled.div`
  height: 100%;
  width: 100%;

  .mapboxgl-map,
  .mapboxgl-canvas-container,
  .mapboxgl-canvas {
    height: 100%;
    width: 100%;
    display: block;
  }
`;

// Vertical button group on the right edge, just below the mapbox navigation control.
// Groups zoom-to-extents, the player link, and the shadow-track download into one connected control.
const MapControlGroup = styled.div`
  position: absolute;
  top: 104px;
  right: 10px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  /* Hairline divider between adjacent buttons, regardless of element type. */
  > * + * {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }
`;

const MapControlButton = styled.button`
  width: 29px;
  height: 29px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: none;
  padding: 0;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.75);
  font-size: 14px;
  text-decoration: none;

  &:hover {
    color: #4096ff;
  }
`;

const MapControlLink = MapControlButton.withComponent('a');
